/*
  Overlook quick add.
  Adds a small "+" button beside each product name on product cards.
  The button opens a short panel that asks for size and, for coffee, grind,
  then adds the item to the cart with the same "Style" line-item property
  the product page uses, so every order still shows the grind.
*/
(() => {
  if (window.__overlookQuickAdd) return;
  window.__overlookQuickAdd = true;

  const GRINDS = [
    'Whole Bean',
    'Cold Brew (Coarse)',
    'French Press (Coarse)',
    'Pour Over (Medium)',
    'Filter Coffee (Medium)',
    'Espresso (Fine)',
  ];
  const SIZE_ORDER = ['4 oz', '12 oz', '2 lb', '5 lb'];
  const cache = new Map();
  const ratings = new Map();

  // Star rating from the product's review metafields, served by templates/product.oqa-data.liquid.
  const loadRating = (handle) => {
    if (!ratings.has(handle)) {
      ratings.set(handle, fetch(`/products/${handle}?view=oqa-data`).then((r) => r.json()).catch(() => null));
    }
    return ratings.get(handle);
  };

  const showRating = async (handle) => {
    const el = dialog.querySelector('.oqa__rating');
    el.hidden = true;
    el.textContent = '';
    const data = await loadRating(handle);
    if (!data || !data.count || !data.rating || !current || current.product.handle !== handle) return;
    const rating = Math.max(0, Math.min(5, Number(data.rating)));
    const stars = document.createElement('span');
    stars.className = 'oqa__stars';
    stars.style.setProperty('--oqa-rating', rating);
    stars.setAttribute('aria-hidden', 'true');
    stars.textContent = '★★★★★';
    const label = document.createElement('span');
    label.className = 'oqa__rating-text';
    label.textContent = `${rating.toFixed(1)} (${data.count} ${data.count === 1 ? 'review' : 'reviews'})`;
    el.setAttribute('aria-label', `Rated ${rating.toFixed(1)} out of 5 from ${data.count} reviews`);
    el.append(stars, label);
    el.hidden = false;
  };

  const money = (cents) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  };

  const isCoffee = (p) => /coffee/i.test(p.type || '') && !/subscription/i.test(p.title);

  const loadProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}products/${handle}.js`).then((r) => {
          if (!r.ok) throw new Error('Product not found');
          return r.json();
        })
      );
    }
    return cache.get(handle);
  };

  /* ---------- the panel ---------- */
  let dialog;
  const buildDialog = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'oqa';
    dialog.setAttribute('aria-labelledby', 'oqa-title');
    dialog.innerHTML = `
      <form method="dialog" class="oqa__inner">
        <button type="button" class="oqa__close" aria-label="Close">&times;</button>
        <p class="oqa__title" id="oqa-title"></p>
        <p class="oqa__rating" hidden></p>
        <p class="oqa__price"></p>
        <fieldset class="oqa__field oqa__sizes">
          <legend>Size</legend>
          <div class="oqa__pills"></div>
        </fieldset>
        <label class="oqa__field oqa__grind">
          <span>Grind</span>
          <select name="grind"></select>
        </label>
        <p class="oqa__error" role="alert" hidden></p>
        <button type="submit" class="oqa__add button">Add to cart</button>
        <a class="oqa__details" href="#">View full details</a>
      </form>`;
    document.body.appendChild(dialog);
    dialog.querySelector('.oqa__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    dialog.querySelector('select').innerHTML = GRINDS.map((g) => `<option>${g}</option>`).join('');
    dialog.querySelector('form').addEventListener('submit', onSubmit);
  };

  let current = null;

  const sizeIndex = (p) => {
    const i = (p.options || []).findIndex((o) => /size|weight/i.test(o.name || o));
    return i === -1 ? 0 : i;
  };

  const variantFor = (p, value) => {
    const i = sizeIndex(p);
    return p.variants.find((v) => v.options[i] === value);
  };

  const render = () => {
    const { product: p, variant } = current;
    dialog.querySelector('.oqa__title').textContent = p.title;
    dialog.querySelector('.oqa__price').textContent = money(variant.price);
    dialog.querySelector('.oqa__details').href = `/products/${p.handle}`;
    const add = dialog.querySelector('.oqa__add');
    add.disabled = !variant.available;
    add.textContent = variant.available ? 'Add to cart' : 'Sold out';
    dialog.querySelectorAll('.oqa__pill').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === variant.options[sizeIndex(p)])));
  };

  const open = async (handle, trigger) => {
    if (!dialog) buildDialog();
    trigger.classList.add('is-loading');
    loadRating(handle);
    try {
      const p = await loadProduct(handle);
      // Subscriptions need a delivery plan, which is chosen on the product page.
      if (p.requires_selling_plan) { window.location.href = `/products/${handle}`; return; }
      const hasSizes = !(p.variants.length === 1 && /default title/i.test(p.variants[0].title));
      const i = sizeIndex(p);
      const values = hasSizes
        ? [...new Set(p.variants.map((v) => v.options[i]))].sort((a, b) => {
            const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
          })
        : [];
      const preferred = p.variants.find((v) => v.available && /12 oz/i.test(v.title)) || p.variants.find((v) => v.available) || p.variants[0];
      current = { product: p, variant: preferred, coffee: isCoffee(p) };

      const pills = dialog.querySelector('.oqa__pills');
      pills.innerHTML = '';
      values.forEach((value) => {
        const v = variantFor(p, value);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'oqa__pill';
        b.dataset.value = value;
        b.textContent = value;
        if (!v || !v.available) b.classList.add('is-unavailable');
        b.addEventListener('click', () => { if (v) { current.variant = v; render(); } });
        pills.appendChild(b);
      });
      dialog.querySelector('.oqa__sizes').hidden = !hasSizes;
      dialog.querySelector('.oqa__grind').hidden = !current.coffee;
      dialog.querySelector('select').value = 'Whole Bean';
      dialog.querySelector('.oqa__error').hidden = true;
      render();
      dialog.showModal();
      showRating(p.handle);
    } catch (e) {
      window.location.href = `/products/${handle}`;
    } finally {
      trigger.classList.remove('is-loading');
    }
  };

  async function onSubmit(e) {
    e.preventDefault();
    const add = dialog.querySelector('.oqa__add');
    const err = dialog.querySelector('.oqa__error');
    add.disabled = true;
    add.classList.add('is-loading');
    err.hidden = true;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const body = { id: current.variant.id, quantity: 1 };
    if (current.coffee) body.properties = { Style: dialog.querySelector('select').value };
    if (cart && cart.getSectionsToRender) {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    try {
      const res = await fetch(`${(window.routes && routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      dialog.close();
      if (cart && cart.renderContents && data.sections) {
        cart.renderContents(data);
      } else {
        window.location.href = (window.routes && routes.cart_url) || '/cart';
      }
    } catch (error) {
      err.textContent = error.message;
      err.hidden = false;
    } finally {
      add.classList.remove('is-loading');
      add.disabled = !current.variant.available;
    }
  }

  /* ---------- the "+" buttons on cards ---------- */
  const decorate = (root = document) => {
    root.querySelectorAll('.card-wrapper').forEach((card) => {
      if (card.dataset.oqa) return;
      const info = card.querySelector('.card > .card__content .card__information');
      const link = info && info.querySelector('.card__heading a[href*="/products/"]');
      if (!info || !link) return;
      card.dataset.oqa = '1';
      const handle = new URL(link.href, location.origin).pathname.split('/products/')[1].split(/[/?#]/)[0];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'oqa-trigger';
      btn.setAttribute('aria-label', `Quick add ${link.textContent.trim()}`);
      btn.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 4v12M4 10h12"/></svg>';
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); open(handle, btn); });
      info.classList.add('oqa-info');
      info.appendChild(btn);
    });
  };

  const start = () => {
    decorate();
    new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach((n) => { if (n.nodeType === 1) decorate(n); });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
